import { Alert, Form, Input, InputNumber, Modal, Space, Typography } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { serialLabel } from './voucherPrintLayout'
import {
  VOUCHER_REASON_MAX,
  voidVoucherRange,
  type VoucherBatch,
  type VoucherRangeVoidResult,
} from './voucherApi'

interface VoucherRangeVoidModalProps {
  /** `null` — oyna yopiq */
  batch: VoucherBatch | null
  onClose: () => void
  onVoided: (batchNo: number, result: VoucherRangeVoidResult) => void
}

interface FormValues {
  fromSerial: number
  toSerial: number
  reason: string
}

/**
 * Partiya ichidagi seriya oralig'ini bekor qilish (ADR-056 §5) — qaytarib bo'lmaydi.
 * Sabab majburiy: u har kartaga yoziladi va reyestrda «nega bekor qilingan» savoliga javob beradi.
 */
export function VoucherRangeVoidModal({ batch, onClose, onVoided }: VoucherRangeVoidModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()
  const quantity = batch?.quantity ?? 1

  const voidRange = useMutation({
    mutationFn: (values: FormValues) =>
      voidVoucherRange(
        batch?.id ?? '',
        { fromSerial: values.fromSerial, toSerial: values.toSerial },
        values.reason.trim(),
      ),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.vouchers.batches })
      void queryClient.invalidateQueries({ queryKey: queryKeys.vouchers.register })
      void queryClient.invalidateQueries({ queryKey: queryKeys.vouchers.vispReport })
      form.resetFields()
      if (batch) onVoided(batch.batchNo, result)
      onClose()
    },
  })

  const close = () => {
    form.resetFields()
    voidRange.reset()
    onClose()
  }

  const from = Form.useWatch('fromSerial', form)
  const to = Form.useWatch('toSerial', form)
  const picked = from != null && to != null && to >= from ? { from, to } : null

  return (
    <Modal
      open={batch !== null}
      title={batch ? t('voucher.rangeVoid.titleNo', { no: batch.batchNo }) : t('voucher.batch.voidRange')}
      okText={t('voucher.batch.voidRange')}
      cancelText={t('common.cancel')}
      okButtonProps={{ danger: true }}
      confirmLoading={voidRange.isPending}
      onCancel={close}
      destroyOnHidden
      width={520}
      onOk={() => submitOnValid(form, (values) => voidRange.mutate(values))}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          type="warning"
          showIcon
          message={t('voucher.batch.voidWarningTitle')}
          description={t('voucher.rangeVoid.warning')}
        />

        {voidRange.isError ? (
          <Alert type="error" showIcon message={translateError(voidRange.error, t)} />
        ) : null}

        <Form<FormValues> form={form} layout="vertical" requiredMark preserve={false} disabled={voidRange.isPending}>
          <Space align="start" wrap>
            <Form.Item
              name="fromSerial"
              label={t('voucher.rangeVoid.from')}
              rules={[{ required: true, message: t('voucher.rangeVoid.fromRequired') }]}
            >
              <InputNumber<number> min={1} max={quantity} precision={0} autoFocus style={{ width: 160 }} />
            </Form.Item>
            <Form.Item
              name="toSerial"
              label={t('voucher.rangeVoid.to')}
              dependencies={['fromSerial']}
              rules={[
                { required: true, message: t('voucher.rangeVoid.toRequired') },
                ({ getFieldValue }) => ({
                  validator: (_, value: number | null) => {
                    const start = getFieldValue('fromSerial') as number | null
                    return value == null || start == null || value >= start
                      ? Promise.resolve()
                      : Promise.reject(new Error(t('voucher.rangeVoid.order')))
                  },
                }),
              ]}
            >
              <InputNumber<number> min={1} max={quantity} precision={0} style={{ width: 160 }} />
            </Form.Item>
          </Space>

          <Typography.Paragraph type="secondary" style={{ marginTop: -8 }}>
            {batch && picked
              ? t('voucher.rangeVoid.picked', {
                  first: serialLabel(batch.batchNo, picked.from, quantity),
                  last: serialLabel(batch.batchNo, picked.to, quantity),
                  count: picked.to - picked.from + 1,
                })
              : t('voucher.rangeVoid.hint', { quantity })}
          </Typography.Paragraph>

          <Form.Item
            name="reason"
            label={t('actions.reason')}
            extra={t('voucher.voidReasonHint')}
            rules={[
              { required: true, whitespace: true, message: t('voucher.voidReasonRequired') },
              { max: VOUCHER_REASON_MAX, message: t('voucher.voidReasonTooLong') },
            ]}
          >
            <Input.TextArea rows={2} maxLength={VOUCHER_REASON_MAX} showCount />
          </Form.Item>
        </Form>
      </Space>
    </Modal>
  )
}
